/*
 * <anzu-rail> — the next-card control for the Anzu A+ card rail.
 *
 * The rail itself is native overflow scrolling with scroll-snap, so swipe, keyboard and
 * momentum all work with no JS. This only adds the chevron affordance from the design:
 * it advances by one card and hides once there's nothing left to reveal, so the control
 * never lies about what's there.
 *
 * Above the rail breakpoint the cards become a plain grid and the control stays hidden, unless
 * the section asks for one of the two pinned desktop behaviours by name.
 *
 * With "Advance with scroll" on, the section pins instead: progress down a tall track maps
 * linearly onto scrollLeft, so the cards step sideways under the reader's thumb and the next
 * section begins once the last one has landed. Swipe is off in that mode (CSS) — two scroll
 * axes competing on one element is what makes a nested carousel feel broken on touch — and the
 * chevron retires with it, since a control that duplicates the page scroll is one more thing
 * that can disagree with it.
 *
 * "Rail" on desktop is that same mechanism in a bigger box: the section takes the whole screen,
 * the intro is held to one side, and the row runs past it. Nothing below changes for it — the
 * driver already maps progress onto scrollLeft, and CSS decides how wide the run is.
 *
 * scrollLeft rather than a transform: the gutter bleed, the card widths and the reduced-motion
 * fallback then all work off exactly the same box the swipeable rail already uses.
 *
 * The tie to the scroll is rigid on a phone and eased on a desktop, and that is a statement
 * about the two inputs rather than a preference. A thumb is already smooth, and easing under a
 * finger reads as the rail sliding around on its own. A wheel is not smooth — it arrives in
 * coarse, uneven jumps — so the row eases toward wherever the scroll last put it. Both land in
 * exactly the same place; only the way they get there differs.
 */
(function () {
  var EDGE = 4;      // px of slack when deciding "already at the end"
  var GLIDE = 0.22;  // share of the distance left to the target closed each frame
  var RUN = 0.12;    // share of the visible row that must be off-screen to be worth pinning

  class AnzuRail extends HTMLElement {
    connectedCallback() {
      if (this.dataset.railReady) return;
      this.dataset.railReady = '1';

      this.scroller = this.querySelector('[data-rail-scroller]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.scroller || !this.next) return;

      this.track = this.closest('[data-rail-track]');
      this.stage = this.closest('[data-rail-stage]');
      this.cards = [].slice.call(this.scroller.querySelectorAll('.aplus-rail__card'));
      this.mq = window.matchMedia ? matchMedia('(max-width: 749px)') : null;
      this.lastX = -1;
      this.ticking = false;

      this.onScroll = this.sync.bind(this);
      this.onNext = this.advance.bind(this);
      this.onPage = this.onPageScroll.bind(this);
      this.frame = this.drive.bind(this);
      this.onGlide = this.glide.bind(this);

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.next.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onScroll, { passive: true });

      // Diagnostics: add ?raildebug to the URL to print what the runtime actually measures.
      // Gated on the query string, so it can never render for a shopper.
      if (location.search.indexOf('raildebug') > -1) {
        this.dbg = document.createElement('pre');
        this.dbg.setAttribute('style',
          'position:fixed;left:0;bottom:0;z-index:99999;margin:0;padding:6px 8px;' +
          'font:10px/1.35 monospace;color:#ffd400;background:rgba(0,0,0,.88);white-space:pre;' +
          'pointer-events:none;max-width:70vw');
        document.body.appendChild(this.dbg);
      }

      if (this.track && this.stage) {
        this.section = this.closest('.aplus-rail');
        this.sizeTrack();
        window.addEventListener('scroll', this.onPage, { passive: true });
        window.addEventListener('resize', this.onPage, { passive: true });
        this.onPageScroll();
      }
      this.sync();
    }

    disconnectedCallback() {
      if (!this.scroller) return;
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.next.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('scroll', this.onPage);
      window.removeEventListener('resize', this.onPage);
      this.stopGlide();
    }

    // Which behaviour this breakpoint gets, or null for none. The two sides of the breakpoint do
    // different things: below it the cards step sideways under the thumb, above it they stack and
    // replace each other. Each is opt-in per section, and neither runs when the reader has asked
    // for less motion — the CSS unpins under exactly the same conditions.
    mode() {
      if (!this.track || !this.stage) return null;
      if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
      var narrow = this.mq ? this.mq.matches : false;
      // Below the breakpoint there is only one thing the pin can mean. Above it the section
      // says which of the two desktop behaviours it wants, by name.
      if (narrow) return this.track.dataset.railPinMobile === 'true' ? 'rail' : null;
      var want = this.track.dataset.railPinDesktop;
      return want === 'rail' || want === 'stack' ? want : null;
    }

    pinned() { return this.mode() !== null; }

    onPageScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this.frame);
    }

    // Whether the desktop row has anywhere to go. It has to be measured with the driven layout
    // applied, because that layout is what creates the overflow — the grid it falls back to has
    // none — so the answer is cached against the viewport size rather than recomputed on every
    // scroll frame, which would cost a forced reflow each time.
    rowOverflows() {
      var key = window.innerWidth + 'x' + window.innerHeight;
      if (this.fitKey === key) return this.fitAns;
      this.fitKey = key;
      if (!this.section) return (this.fitAns = false);
      var had = this.section.classList.contains('is-driven');
      this.section.classList.add('is-driven');
      // Not just "overflows at all": a row that overruns by a few pixels would pin the whole
      // section and then nudge, which reads worse than never pinning. It has to be a run.
      var over = this.scroller.scrollWidth - this.scroller.clientWidth;
      this.fitAns = over > Math.max(EDGE, this.scroller.clientWidth * RUN);
      if (!had) this.section.classList.remove('is-driven');
      return this.fitAns;
    }

    // The track is only a scroll budget, so it is the stage's own height plus however much
    // scrolling the cards are meant to take. Sized here rather than in CSS because the stage
    // is content-height — pinning it to a viewport left a screenful of dead space around a
    // rail that is barely half that tall.
    //
    // It also gates is-driven, which is what switches swipe off: if this never runs, the
    // reader is left with the ordinary swipeable rail rather than one that does neither.
    sizeTrack() {
      var on = this.pinned();
      // A screen wide enough to show every card has nothing to walk through. Pinning there would
      // hold the reader on a dead screen for a whole track's worth of scrolling — the kind of
      // failure nobody reports because nothing looks broken. Fall back to the grid instead.
      if (on && this.mode() === 'rail' && !(this.mq && this.mq.matches) && !this.rowOverflows()) {
        on = false;
      }
      if (this.section) this.section.classList.toggle('is-driven', on);
      if (!on) {
        this.track.style.height = '';
        this.stage.style.top = '';
        if (this.section) this.section.style.removeProperty('--rail-p');
        this.stopGlide();
        return;
      }
      var narrow = this.mq ? this.mq.matches : false;
      var attr = narrow ? 'data-rail-advance' : 'data-rail-advance-desktop';
      var advance = parseFloat(this.track.getAttribute(attr));
      if (!(advance > 0)) advance = parseFloat(this.track.getAttribute('data-rail-advance')) || 0;
      var stageH = this.stage.offsetHeight;
      this.track.style.height = Math.round(stageH + (advance / 100) * window.innerHeight) + 'px';

      // Centre the held rail in the band the reader can actually see — between the sticky
      // header and the dock. Sticking it at the top instead leaves the whole leftover height
      // as one dead gap underneath it; splitting it reads as deliberate framing.
      var cs = getComputedStyle(document.documentElement);
      var head = parseFloat(cs.getPropertyValue('--sticky-header-height')) || 0;
      var dock = parseFloat(cs.getPropertyValue('--mobile-dock-height')) || 0;
      //
      // A stage that already fills the band has no leftover to split: it rests at the very top
      // and clears the header with its own padding instead. Centring one of those would push it
      // down by the header's height and hang the same amount off the bottom of the screen.
      var band = window.innerHeight - head - dock;
      var top = stageH >= band ? 0 : head + Math.max(0, (band - stageH) / 2);
      this.stage.style.top = Math.round(top) + 'px';
    }

    // Linear: equal thumb travel, equal card travel. Easing would break that tie and read as
    // the rail lagging behind the finger.
    drive() {
      this.ticking = false;
      this.sizeTrack();
      var mode = this.mode();
      if (!mode) { this.report(); return; }
      var rect = this.track.getBoundingClientRect();
      var span = rect.height - this.stage.offsetHeight;
      if (span <= 0) { this.report(); return; }
      // The stage sticks at `top`, not at 0, so that is where progress starts from.
      var stickyTop = parseFloat(this.stage.style.top) || 0;
      var p = (stickyTop - rect.top) / span;
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      // The desktop rail's progress line reads this. Written as the same untransitioned,
      // scroll-linked number the cards move on, so the two can never disagree.
      if (this.section) this.section.style.setProperty('--rail-p', p.toFixed(4));

      if (mode === 'stack') { this.stackTo(p); this.report(); return; }

      var max = this.scroller.scrollWidth - this.scroller.clientWidth;
      if (max <= 0) { this.report(); return; }
      var x = p * max;
      this.report();

      // A thumb is already smooth, so the phone keeps the rigid tie — easing under a finger
      // reads as the rail sliding around on its own. A wheel is not: it arrives in coarse,
      // uneven jumps, and tying the row rigidly to it reproduces every one of them.
      if (this.mq && this.mq.matches) {
        var r = Math.round(x);
        if (r === this.lastX) return;
        this.lastX = r;
        this.scroller.scrollLeft = r;
        return;
      }
      this.targetX = x;
      this.startGlide();
    }

    /* Ease the row toward wherever the scroll last put it, rather than snapping to it. This
       smooths the input without disconnecting the two: at GLIDE a frame the row is 95% of the
       way there inside about 200ms, which reads as weight rather than as lag. It changes only
       HOW the row gets there — the target is the same linear map the phone uses, so both
       breakpoints still land in exactly the same place. */
    startGlide() {
      if (this.gliding) return;
      this.gliding = true;
      this.curX = this.scroller.scrollLeft;
      this.glideRaf = requestAnimationFrame(this.onGlide);
    }

    glide() {
      var d = this.targetX - this.curX;
      if (Math.abs(d) < 0.4) {
        this.curX = this.targetX;
        this.scroller.scrollLeft = this.curX;
        this.gliding = false;
        return;
      }
      this.curX += d * GLIDE;
      this.scroller.scrollLeft = this.curX;
      this.glideRaf = requestAnimationFrame(this.onGlide);
    }

    stopGlide() {
      this.gliding = false;
      if (this.glideRaf) cancelAnimationFrame(this.glideRaf);
      this.glideRaf = 0;
    }

    // Each card rises from below the stage to cover the one before it. Progress is split into
    // one segment per gap between cards, so card k travels over segment k and is simply parked
    // — landed, or still below — for every other segment.
    //
    // Card 0 resolves to 1 for every p, which is what keeps the first card in place from the
    // start rather than sliding in from nowhere.
    stackTo(p) {
      var n = this.cards.length;
      if (n < 2) return;
      for (var k = 0; k < n; k++) {
        var lp = p * (n - 1) - (k - 1);
        lp = lp < 0 ? 0 : lp > 1 ? 1 : lp;
        var card = this.cards[k];
        card.style.setProperty('--card-in', (1 - lp).toFixed(4));
        // Later cards sit over earlier ones — that IS the effect. Set here because CSS cannot
        // count an arbitrary number of blocks without a rule per index.
        if (card.style.zIndex !== String(k + 1)) card.style.zIndex = String(k + 1);
      }
    }

    // Reports the four things that decide whether this works at all: whether it engaged, how
    // much scroll the pin actually has, how far the row can travel, and where it currently is.
    report() {
      if (!this.dbg) return;
      var out = [];
      var mqOk = !this.mq || this.mq.matches;
      var calm = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
      out.push('viewport ' + window.innerWidth + 'x' + window.innerHeight +
        '   mq(<=749) ' + mqOk + '   reduced-motion ' + calm);
      out.push('track ' + (this.track ? 'found' : 'MISSING') + '   stage ' + (this.stage ? 'found' : 'MISSING') +
        '   pinned() ' + this.pinned());
      if (this.track && this.stage) {
        var r = this.track.getBoundingClientRect();
        var span = r.height - this.stage.offsetHeight;
        out.push('track h ' + Math.round(r.height) + '   stage h ' + Math.round(this.stage.offsetHeight) +
          '   span ' + Math.round(span) + (span <= 0 ? '   <-- NO PIN (track not taller than stage)' : ''));
        out.push('track top ' + Math.round(r.top));
        var cs = getComputedStyle(this.stage);
        out.push('stage position ' + cs.position + '   top ' + cs.top +
          (cs.position !== 'sticky' ? '   <-- NOT STICKY' : ''));
      }
      var max = this.scroller.scrollWidth - this.scroller.clientWidth;
      out.push('scrollWidth ' + this.scroller.scrollWidth + '   clientWidth ' + this.scroller.clientWidth +
        '   max ' + max + (max <= 0 ? '   <-- CARDS DO NOT OVERFLOW' : ''));
      out.push('scrollLeft ' + Math.round(this.scroller.scrollLeft) + '   overflow-x ' +
        getComputedStyle(this.scroller).overflowX);
      this.dbg.textContent = out.join(String.fromCharCode(10));
    }

    step() {
      var card = this.scroller.querySelector('.aplus-rail__card');
      if (!card) return this.scroller.clientWidth;
      var gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    atEnd() {
      var s = this.scroller;
      // No overflow at all (desktop grid, or fewer cards than fit) counts as "at the end".
      return s.scrollWidth - s.clientWidth <= EDGE ||
             s.scrollLeft >= s.scrollWidth - s.clientWidth - EDGE;
    }

    sync() {
      // While the rail is driven from the page there is nothing for a next control to do that
      // the scroll is not already doing.
      var done = this.pinned() || this.atEnd();
      this.next.hidden = done;
      this.next.setAttribute('aria-hidden', done ? 'true' : 'false');
      this.next.tabIndex = done ? -1 : 0;
    }

    advance() {
      this.scroller.scrollBy({
        left: this.step(),
        behavior: (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)
          ? 'auto'
          : 'smooth',
      });
    }
  }

  if (!customElements.get('anzu-rail')) {
    customElements.define('anzu-rail', AnzuRail);
  }

  window.theme = window.theme || {};
  window.theme.AnzuRail = AnzuRail;
})();
